import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { ENV } from '@di-tokens';
import {
  AmountPayload,
  OrderAddressPayload,
  OrderPlacingResponse,
  PaymentAuthorizationBody,
  PaymentAuthorizationResp,
  PlaceOrderHeaderEnum,
  PlaceOrderPayload,
  SubmitPayloadId,
  SubmitPaymentPayload,
} from '@models';
import { Observable, tap } from 'rxjs';

import { decodeTransientCard } from '../utils/transient-token.utils';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  private amounts = signal<AmountPayload | null>(null);
  private email = signal<string>('');
  private payment = signal<SubmitPayloadId | null>(null);
  private shippingAddress = signal<OrderAddressPayload | null>(null);
  private billingAddress = signal<OrderAddressPayload | null>(null);
  private placeOrderPayload = computed<PlaceOrderPayload | null>(() => {
    if (this.amounts()) {
      return {
        amount: this.amounts()!,
        payment: this.payment()?.payloadId ? this.payment() : {},
        shippingAddress: this.shippingAddress(),
        billingAddress: this.billingAddress(),
        email: this.email(),
      };
    }
    return null;
  });
  captureContextToken = signal<string | null>(null);
  transientToken = signal<string | null>(null);
  /** RCP-466 — brand and last digits of the entered card, for the order review. */
  readonly transientCard = computed(() => {
    const token = this.transientToken();
    return token ? decodeTransientCard(token) : null;
  });

  private http = inject(HttpClient);
  private env = inject(ENV);

  setPaymentInfo(data: SubmitPaymentPayload): void {
    this.amounts.set({
      amountPoints: data.pointsRedeem,
      amountUsd: data.amountUsd,
    });
    this.shippingAddress.set(data.shippingAddress ?? null);
    this.billingAddress.set(data.billingAddress ?? null);
    this.email.set(data.email ?? '');
  }

  setPayerInfo(payload: SubmitPayloadId): void {
    this.payment.set(payload);
  }

  setCaptureContextToken(captureContext: string): void {
    this.captureContextToken.set(captureContext);
  }

  setTransientToken(token: string): void {
    this.transientToken.set(token);
  }

  placeOrder(): Observable<OrderPlacingResponse> {
    const captureContextToken = this.captureContextToken();
    const transientToken = this.transientToken();
    const payload = this.placeOrderPayload();
    let headers = new HttpHeaders();
    if (captureContextToken) {
      headers = headers.set(PlaceOrderHeaderEnum.CaptureContext, captureContextToken as string);
    }

    if (transientToken) {
      headers = headers.set(PlaceOrderHeaderEnum.TransientToken, transientToken as string);
    }

    return this.http
      .post<OrderPlacingResponse>(`${this.env.remoteApi}/orders`, payload, {
        headers,
      })
      .pipe(tap(() => this.dropPaymentInfo()));
  }

  private dropPaymentInfo(): void {
    this.amounts.set(null);
    this.payment.set({ payloadId: '' });
    this.captureContextToken.set(null);
    this.transientToken.set(null);
  }

  authorize(body: PaymentAuthorizationBody): Observable<PaymentAuthorizationResp> {
    const captureContextToken = this.captureContextToken();
    const transientToken = this.transientToken();
    let headers = new HttpHeaders();

    if (captureContextToken) {
      headers = headers.set(PlaceOrderHeaderEnum.CaptureContext, captureContextToken as string);
    }

    if (transientToken) {
      headers = headers.set(PlaceOrderHeaderEnum.TransientToken, transientToken as string);
    }

    return this.http.post(`${this.env.remoteApi}/payments/authorize`, body, {
      headers,
    }) as unknown as Observable<PaymentAuthorizationResp>;
  }
}
